import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useInviteVerification } from "@/hooks/useInviteVerification";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface InvitePasswordCreationFormProps {
  invitation: any;
}

export function InvitePasswordCreationForm({ invitation }: InvitePasswordCreationFormProps) {
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const { createAccount, isCreatingAccount } = useInviteVerification();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (displayName.length < 2) {
      setError("Display name must be at least 2 characters long");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const success = await createAccount(password, invitation, displayName);
    if (success) {
      // signInWithPassword in createAccount triggers onAuthStateChange,
      // which updates AuthContext → Auth.tsx redirects to "/" → RoleRedirect
      // handles routing to /portal for client_viewer users.
      // Fallback: if auth state doesn't update within 2s, navigate manually.
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    }
  };

  const getPasswordStrength = (pass: string) => {
    if (pass.length < 8) return { label: "Too short", color: "text-destructive" };
    if (pass.length < 12) return { label: "Fair", color: "text-yellow-500" };
    return { label: "Strong", color: "text-green-500" };
  };

  const strength = getPasswordStrength(password);

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Create Your Password</CardTitle>
        <CardDescription>
          Welcome, {invitation?.email}! Set up your account password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="John Doe"
              required
              disabled={isCreatingAccount}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              disabled={isCreatingAccount}
            />
            {password && (
              <p className={`text-sm ${strength.color}`}>
                Strength: {strength.label}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              required
              disabled={isCreatingAccount}
            />
            {confirmPassword && password === confirmPassword && (
              <div className="flex items-center gap-1 text-sm text-green-500">
                <CheckCircle2 className="h-4 w-4" />
                Passwords match
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isCreatingAccount || displayName.length < 2 || password.length < 8 || password !== confirmPassword}
          >
            {isCreatingAccount ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
